import React, { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { useData } from '../../context/DataContext';
import { computeDotPlotData } from '../../utils/dotPlotData';
import { generatePDF } from '../../utils/pdfExport';
import { SingleGeneStrip } from '../StripPlot/StripPlot';


/**
 * Compute separator positions and group headers for the report dot plot.
 */
function getReportDecorations(nCols, nGenes, visibleIndices) {
  const groupBoundaries = [
    { label: "ImmGen (Yoshida '19)", origIndices: [0, 1, 2, 3] },
    { label: 'A20', origIndices: [4, 5] },
    { label: 'EmBO Genotypes', origIndices: [6, 7, 8] },
    { label: 'EmBO Transformed', origIndices: [9, 10, 11] },
  ];

  const shapes = [];
  const annotations = [];
  const origToVisible = {};
  visibleIndices.forEach((orig, vis) => { origToVisible[orig] = vis; });

  let prevGroupEnd = -1;
  for (const group of groupBoundaries) {
    const visibleInGroup = group.origIndices
      .filter(i => origToVisible[i] !== undefined)
      .map(i => origToVisible[i]);

    if (visibleInGroup.length === 0) continue;
    const groupStart = Math.min(...visibleInGroup);
    const groupEnd = Math.max(...visibleInGroup);

    if (prevGroupEnd >= 0 && groupStart > prevGroupEnd) {
      const sepX = (prevGroupEnd + groupStart) / 2;
      shapes.push({
        type: 'line',
        x0: sepX, x1: sepX,
        y0: -0.5, y1: nGenes - 0.5,
        line: { color: '#333', width: 1.5 },
        xref: 'x', yref: 'y',
      });
    }

    const centerX = (groupStart + groupEnd) / 2;
    annotations.push({
      x: centerX, y: -1.5,
      text: group.label,
      showarrow: false,
      font: { size: 11, color: '#777' },
      xref: 'x', yref: 'y',
    });

    prevGroupEnd = groupEnd;
  }

  return { separatorShapes: shapes, groupAnnotations: annotations };
}

export default function ReportBuilder({ reportGenes, reportColorMode }) {
  const { data, getFilteredData } = useData();
  const [generating, setGenerating] = useState(false);

  const { filteredExpression, filteredColLabels, visibleIndices } = useMemo(
    () => getFilteredData(),
    [getFilteredData]
  );

  const plotData = useMemo(() => {
    if (!data || reportGenes.length === 0) return null;
    return computeDotPlotData({
      genes: reportGenes,
      expression: filteredExpression,
      colLabels: filteredColLabels,
      colorMode: reportColorMode,
    });
  }, [data, reportGenes, reportColorMode, filteredExpression, filteredColLabels]);

  if (!data) return null;

  if (reportGenes.length === 0) {
    return (
      <div className="report-empty">
        <h3>Custom Dot Plot Report</h3>
        <p>Paste a list of genes in the sidebar to generate a publication-quality dot plot.</p>
        <p className="hint">Supports comma, tab, or newline-separated gene names. Aliases like Mll2 are automatically resolved.</p>
      </div>
    );
  }

  if (!plotData) return null;

  const nGenes = plotData.genes.length;
  const nCols = plotData.nCols;
  const plotHeight = Math.max(400, nGenes * 28 + 150);

  const { separatorShapes, groupAnnotations } = getReportDecorations(
    nCols, nGenes, visibleIndices
  );

  // Missing gene rows
  const missingShapes = plotData.missingRows.map(i => ({
    type: 'rect',
    x0: -0.5, x1: nCols - 0.5,
    y0: i - 0.4, y1: i + 0.4,
    fillcolor: '#f0f0f0',
    line: { color: '#ccc', width: 0.5, dash: 'dot' },
    xref: 'x', yref: 'y',
  }));

  const missingAnnotations = plotData.missingRows.map(i => ({
    x: nCols / 2, y: i,
    text: '<i>no murine ortholog</i>',
    showarrow: false,
    font: { size: 10, color: '#999' },
    xref: 'x', yref: 'y',
  }));

  // Size legend traces
  const sizeLegendTraces = plotData.sizeRefVals.map(v => {
    const markerSize = Math.max(4, (v / plotData.maxLog) * 30);
    return {
      type: 'scatter',
      mode: 'markers',
      x: [null], y: [null],
      marker: { size: markerSize, color: '#ccc', line: { width: 0.5, color: '#999' } },
      name: `${v.toFixed(0)}`,
      showlegend: true,
      legendgroup: 'size',
      hoverinfo: 'skip',
    };
  });

  const traces = [
    {
      type: 'scatter',
      mode: 'markers',
      x: plotData.x,
      y: plotData.y,
      marker: {
        size: plotData.sizes,
        color: plotData.colors,
        colorscale: plotData.colorscale,
        cmin: plotData.cmin,
        cmax: plotData.cmax,
        colorbar: {
          title: { text: plotData.colorbarTitle, font: { size: 11, color: '#333' } },
          tickfont: { size: 10, color: '#555' },
          len: 0.4,
          thickness: 12,
          x: 1.06,
          outlinewidth: 0,
        },
        line: { width: 0.5, color: '#999' },
      },
      text: plotData.hoverTexts,
      hoverinfo: 'text',
      showlegend: false,
    },
    ...sizeLegendTraces,
  ];

  const layout = {
    width: Math.max(700, 400 + nCols * 50),
    height: plotHeight,
    paper_bgcolor: '#ffffff',
    plot_bgcolor: '#ffffff',
    margin: { l: 120, r: 80, t: 60, b: 30 },
    xaxis: {
      tickvals: [...Array(nCols).keys()],
      ticktext: filteredColLabels,
      side: 'top',
      tickfont: { size: 10, color: '#333' },
      range: [-0.8, nCols],
      gridcolor: '#eee',
      zeroline: false,
      fixedrange: true,
      showline: true,
      linecolor: '#ccc',
      linewidth: 1,
    },
    yaxis: {
      tickvals: [...Array(nGenes).keys()],
      ticktext: plotData.genes.map(g => `<i>${g}</i>`),
      autorange: 'reversed',
      tickfont: { size: 10, color: '#333', family: 'Inter' },
      gridcolor: '#eee',
      zeroline: false,
      fixedrange: true,
      showline: true,
      linecolor: '#ccc',
      linewidth: 1,
    },
    shapes: [...separatorShapes, ...missingShapes],
    annotations: [...missingAnnotations, ...groupAnnotations],
    hovermode: 'closest',
    legend: {
      title: { text: 'log₂(expr+1)', font: { size: 10, color: '#555' } },
      x: 1.02, y: 0.15,
      font: { size: 9, color: '#555' },
      bgcolor: 'rgba(255,255,255,0)',
      borderwidth: 0,
      tracegroupgap: 0,
      itemsizing: 'constant',
    },
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      await generatePDF({
        genes: reportGenes,
        expression: filteredExpression,
        perSample: data.perSample,
        colLabels: filteredColLabels,
        colorMode: reportColorMode,
        visibleIndices,
      });
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed. See console for details.');
    } finally {
      setGenerating(false);
    }
  };

  // Strip plot preview
  const stripCols = nGenes <= 4 ? 2 : nGenes <= 9 ? 3 : 4;
  const stripCellW = Math.max(260, Math.min(320, 1000 / stripCols));
  const stripCellH = 230;

  return (
    <div className="report-builder">
      <div className="report-header">
        <h3>Custom Report — {nGenes} gene{nGenes !== 1 ? 's' : ''}</h3>
        <button
          className="download-pdf-btn"
          onClick={handleDownload}
          disabled={generating}
        >
          {generating ? 'Generating PDF...' : 'Download PDF'}
        </button>
      </div>

      {/* Dot plot preview */}
      <h4 style={{ color: '#ccc', margin: '16px 0 8px', fontSize: 13 }}>Dot Plot Preview</h4>
      <div className="dot-plot-container">
        <Plot
          data={traces}
          layout={layout}
          config={{ displayModeBar: false, responsive: true }}
        />
      </div>

      {/* Strip plot preview */}
      <h4 style={{ color: '#ccc', margin: '24px 0 8px', fontSize: 13 }}>Strip Plot Preview</h4>
      <div className="strip-grid" style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${stripCols}, ${stripCellW}px)`,
        gap: '4px',
        justifyContent: 'center',
      }}>
        {reportGenes.map(gene => (
          <SingleGeneStrip
            key={gene}
            gene={gene}
            perSample={data.perSample}
            expression9col={data.expression}
            width={stripCellW}
            height={stripCellH}
          />
        ))}
      </div>
    </div>
  );
}
